'use client';

import { useEffect, useRef, useState } from 'react';
import devConsole from '@utils/developer-console';
import { EventResponse } from '@v2/types';
import { Spinner } from '@v2/ui/Icon/Spinner/Spinner';
import { EventCard } from '@v2/views/events/components/EventsList/EventsList';

interface Props {
  title?: string;
  headerText?: string;
  cityLat: number;
  cityLong: number;
  events: EventResponse[];
  totalEvents: number;
}

const PAGE_SIZE = 10;

export const CityPageEventsList = ({
  title,
  headerText,
  cityLat,
  cityLong,
  events,
  totalEvents,
}: Props) => {
  const [loadedEvents, setLoadedEvents] = useState(events);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const nextPage = useRef(3);
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string>();

  useEffect(() => {
    setLoadedEvents(events);
    setVisibleCount(PAGE_SIZE);
    nextPage.current = 3;
    setCanLoadMore(true);
    setLoadError(undefined);
  }, [events]);

  const listOfEvents = loadedEvents.slice(0, visibleCount);
  const isLastPage = visibleCount >= totalEvents || !canLoadMore;
  const itemListId = `city_events_${cityLat}_${cityLong}`;

  const handleShowMore = async () => {
    if (isLoading) return;

    const nextVisibleCount = visibleCount + PAGE_SIZE;

    if (visibleCount < loadedEvents.length) {
      setVisibleCount(
        Math.min(nextVisibleCount, loadedEvents.length, totalEvents)
      );
      return;
    }

    setIsLoading(true);
    try {
      const query = new URLSearchParams({
        lat: String(cityLat),
        lon: String(cityLong),
        page: String(nextPage.current),
      });
      const response = await fetch(`/api/public/events-by-location?${query}`);

      if (!response.ok) throw new Error('Unable to load more city events.');

      const data = (await response.json()) as { events?: EventResponse[] };
      const eventIds = new Set(loadedEvents.map(({ id }) => id));
      const newEvents = (data.events ?? []).filter(({ id }) => {
        if (eventIds.has(id)) return false;
        eventIds.add(id);
        return true;
      });
      const loadedEventCount = loadedEvents.length + newEvents.length;

      setLoadError(undefined);
      setLoadedEvents((currentEvents) => [...currentEvents, ...newEvents]);
      setVisibleCount(
        Math.min(nextVisibleCount, loadedEventCount, totalEvents)
      );
      nextPage.current += 1;
      if (!newEvents.length) setCanLoadMore(false);
    } catch (error) {
      devConsole({ error });
      setLoadError('Unable to load more events. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      id="city-events"
      className="min-w-0"
      aria-labelledby={title ? 'city-events-title' : undefined}
      aria-label={title ? undefined : 'City events'}
    >
      {title && (
        <h2
          id="city-events-title"
          className="text-xl font-bold leading-[30px] text-gray-800 md:text-2xl md:leading-8"
        >
          {title}
        </h2>
      )}
      {headerText && (
        <p className="mt-2 text-sm font-medium leading-5 text-gray-600">
          {headerText}
        </p>
      )}
      <p className="sr-only" role="status">
        {listOfEvents.length} of {totalEvents} events shown
      </p>
      <ul
        id="city-event-list"
        aria-label="Upcoming city events"
        aria-busy={isLoading}
        className="mt-6 flex flex-col gap-6"
      >
        {listOfEvents.map((event, index) => (
          <li key={event.id}>
            <EventCard
              event={event}
              identifier={itemListId}
              actionLabel="View Packages"
              layout="teamSchedule"
              itemListId={itemListId}
              itemListName={title ?? 'City events'}
              itemCategory="City events"
              index={index}
            />
          </li>
        ))}
      </ul>
      {loadError && (
        <p role="alert" className="mt-4 text-sm text-primary-500">
          {loadError}
        </p>
      )}
      {!isLastPage && (
        <button
          type="button"
          onClick={handleShowMore}
          aria-disabled={isLoading}
          aria-controls="city-event-list"
          aria-busy={isLoading}
          className="mt-6 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-800 aria-disabled:cursor-wait aria-disabled:opacity-60"
        >
          {isLoading && (
            <span aria-hidden="true">
              <Spinner size="16" />
            </span>
          )}
          {isLoading ? 'Loading…' : 'Show more'}
        </button>
      )}
    </section>
  );
};
